import { memo, useMemo } from 'react';

import { ChevronDownIcon } from '@air/design-icons';
import { Chip } from '@air/primitive-chip';
import { DropdownMenu, DropdownMenuLabel, DropdownMenuSeparator } from '@air/primitive-dropdown-menu';
import { selectTrigger } from '@air/primitive-select';

import { AssetVersionShareLinkMenuItem } from '~/components/SharePrivateClipModal/AssetVersionShareLinkMenuItem';
import {
  type AssetShareLinkOption,
  getAssetShareLinkSlotDescription,
  isAssetShareLinkEnabled,
} from '~/components/SharePrivateClipModal/buildAssetShareLinkSlots';

export type AssetVersionShareLinkSelectorProps = {
  hideInactiveOptions?: boolean;
  isFetchingShareLinkOptions?: boolean;
  onSelect: (optionKey: string) => void;
  options: AssetShareLinkOption[];
  selectedOptionKey: string;
};

export const AssetVersionShareLinkSelector = memo(
  ({
    hideInactiveOptions = false,
    isFetchingShareLinkOptions = false,
    onSelect,
    options,
    selectedOptionKey,
  }: AssetVersionShareLinkSelectorProps) => {
    const visibleOptions = useMemo(() => {
      if (!hideInactiveOptions) {
        return options;
      }

      return options.filter((option) => isAssetShareLinkEnabled(option.link));
    }, [hideInactiveOptions, options]);

    const currentOptions = useMemo(
      () => visibleOptions.filter((option) => option.slotId === 'current'),
      [visibleOptions],
    );
    const versionOptions = useMemo(
      () => visibleOptions.filter((option) => option.slotId !== 'current'),
      [visibleOptions],
    );

    const selectedOption = useMemo(
      () => options.find((option) => option.key === selectedOptionKey) ?? options[0],
      [options, selectedOptionKey],
    );

    const canSwitchOptions = useMemo(() => {
      if (!hideInactiveOptions) {
        return visibleOptions.length > 1;
      }

      return (
        visibleOptions.length > 1 ||
        (visibleOptions.length === 1 && !visibleOptions.some((option) => option.key === selectedOptionKey))
      );
    }, [hideInactiveOptions, selectedOptionKey, visibleOptions]);

    if (!canSwitchOptions) {
      return null;
    }

    return (
      <div className="flex flex-col gap-2" data-testid="ASSET_VERSION_SHARE_LINK_SELECTOR">
        <p className="font-bold ads:text-metadata text-10 text-gray-10 uppercase tracking-[0.4px]">Asset version</p>
        <div className="flex flex-col gap-1">
          <DropdownMenu
            className="w-[min(100vw-32px,416px)] gap-0 p-2"
            disabled={isFetchingShareLinkOptions}
            trigger={
              <button
                className={selectTrigger({ class: 'w-full rounded-8', size: 'medium' })}
                data-testid="ASSET_VERSION_SHARE_LINK_SELECTOR_TRIGGER"
                type="button"
              >
                <span className="flex min-w-0 flex-1 items-center gap-2">
                  <span className="min-w-0 truncate ads:text-primary text-gray-12">{selectedOption?.title ?? ''}</span>
                  {selectedOption ? (
                    <Chip appearance="filled" className="shrink-0" color="gray" size="small">
                      {selectedOption.chipLabel}
                    </Chip>
                  ) : null}
                </span>
                <ChevronDownIcon className="shrink-0 ads:text-icon-subtle text-gray-9" size="sm" />
              </button>
            }
          >
            {currentOptions.length > 0 ? (
              <div className="flex flex-col gap-2 p-2">
                <DropdownMenuLabel className="px-1 pt-1 tracking-[0.4px]">Current</DropdownMenuLabel>
                {currentOptions.map((option) => (
                  <AssetVersionShareLinkMenuItem
                    chipLabel={option.chipLabel}
                    isEnabled={option.isEnabled}
                    isSelected={selectedOptionKey === option.key}
                    key={option.key}
                    // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
                    onSelect={() => onSelect(option.key)}
                    optionKey={option.key}
                    title={option.title}
                    titleClassName="font-medium"
                  />
                ))}
              </div>
            ) : null}
            {versionOptions.length > 0 ? (
              <>
                {currentOptions.length > 0 ? <DropdownMenuSeparator className="my-0" /> : null}
                <div className="flex flex-col gap-2 p-2">
                  <DropdownMenuLabel className="px-1 pt-1 tracking-[0.4px]">Specific</DropdownMenuLabel>
                  {versionOptions.map((option) => (
                    <AssetVersionShareLinkMenuItem
                      chipLabel={option.chipLabel}
                      isEnabled={option.isEnabled}
                      isSelected={selectedOptionKey === option.key}
                      key={option.key}
                      // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
                      onSelect={() => onSelect(option.key)}
                      optionKey={option.key}
                      title={option.title}
                    />
                  ))}
                </div>
              </>
            ) : null}
          </DropdownMenu>
          <p className="ads:text-metadata text-12 text-gray-10 leading-[1.33]">
            {getAssetShareLinkSlotDescription(selectedOption?.slotId ?? 'current')}
          </p>
        </div>
      </div>
    );
  },
);

AssetVersionShareLinkSelector.displayName = 'AssetVersionShareLinkSelector';
